import { useState } from 'react';

interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  segments: DonutSegment[];
  size?: number;
  strokeWidth?: number;
}

export const DonutChart = ({ segments, size = 220, strokeWidth = 32 }: DonutChartProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total === 0) return null;

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  let cumulativeOffset = 0;

  return (
    <div className="flex flex-col items-center gap-5">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          {segments.map((segment, i) => {
            if (segment.value === 0) return null;
            const pct = segment.value / total;
            const dashLength = pct * circumference;
            const dashOffset = -cumulativeOffset;
            cumulativeOffset += dashLength;
            const isHovered = hoveredIndex === i;

            return (
              <circle
                key={segment.label}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={isHovered ? strokeWidth + 6 : strokeWidth}
                strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                strokeDashoffset={dashOffset}
                strokeLinecap="butt"
                className="transition-all duration-200 cursor-pointer"
                style={{ opacity: hoveredIndex !== null && !isHovered ? 0.5 : 1 }}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {hoveredIndex !== null ? (
            <>
              <span className="text-3xl font-bold text-gray-900">
                {segments[hoveredIndex].value}
              </span>
              <span className="text-xs text-gray-500 mt-0.5 max-w-[80px] text-center leading-tight">
                {segments[hoveredIndex].label}
              </span>
            </>
          ) : (
            <>
              <span className="text-3xl font-bold text-gray-900">{total}</span>
              <span className="text-xs text-gray-500 mt-0.5">produtos</span>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
        {segments.map((segment, i) => (
          <div
            key={segment.label}
            className="flex items-center gap-1.5 cursor-pointer transition-opacity duration-150"
            style={{ opacity: hoveredIndex !== null && hoveredIndex !== i ? 0.4 : 1 }}
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: segment.color }}
            />
            <span className="text-xs text-gray-600">
              {segment.label} ({segment.value})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
